class cartSidebar extends HTMLElement {
    constructor() {
        super();

        this.onBodyClick = this.handleBodyClick.bind(this);
        this.cartButton = document.getElementById("cart-icon-bubble");
        this.cartButton?.addEventListener("click", (event) => {
            event.preventDefault();
            this.toggle(event.target);
        });

        this.bindCartUpdate();
        this.renderSavedItems();
        this.querySelector(".modal__close-button").addEventListener(
            "click",
            this.close.bind(this)
        );
        this.addEventListener("keydown", this.handleKeydown.bind(this));
    }

    handleBodyClick(event) {
        if (
            !this.cartButton.contains(event.target) &&
            !this.contains(event.target)
        ) {
            // unset active element when clicked somewhere on body
            // to prevent page jump back to active element once focus restored
            this.setActiveElement(null);
            this.close();
        }
    }

    handleKeydown(event) {
        if (event.code.toUpperCase() === "ESCAPE") this.close();
    }

    open(openedBy) {
        const focusElement =
            this.querySelector('[name="checkout"]') ||
            this.querySelector(".modal__close-button");

        if (this.dataset.status === "open") return;

        this.dataset.status = "open";
        this.reinitPreloadImages(); // reinit preload images
        document.body.dataset.cartStatus = this.dataset.status;
        if (this.parentNode.tagName === "BODY") {
            document.body.style.width = `${window.visualViewport.width}px`;
            document.body.style.overflow = "hidden";
        }

        document.body.addEventListener("click", this.onBodyClick);

        // add short delay so browser could find element to focus
        // when there is animations it can be tricky for browser
        setTimeout(() => {
            if (openedBy) this.setActiveElement(openedBy);
            trapFocus(this, focusElement);
        }, 100);

        // clean up old messages before output
        this.querySelectorAll(".message-wrapper").forEach((el) => el.remove());
    }

    close() {
        document.body.dataset.cartStatus = this.dataset.status = "close";
        document.body.removeEventListener("click", this.onBodyClick);
        removeTrapFocus(this.activeElement);
        if (!this.closest("header"))
            setTimeout(() => {
                document.body.style.width = "";
                document.body.style.overflow = "";
            }, 200);
    }

    toggle(toggledBy) {
        if (this.dataset.status === "open") this.close();
        else this.open(toggledBy);
    }

    reinitPreloadImages() {
        const cartItems = document.getElementById("cart-sidebar-items");
        const list =
            cartItems && cartItems.querySelectorAll(".media--blur-image");
        if (!list.length) return;

        _preloadImg?.(list, true);
    }

    renderContents(parsedState) {
        this.productId = parsedState.id;
        this.getSectionsToRender().forEach((section) => {
            if (document.getElementById(section.id) !== null) {
                document.getElementById(section.id).innerHTML =
                    this.getSectionInnerHTML(
                        parsedState.sections?.[section.id],
                        section.selector
                    );
            }
        });

        this.renderSavedItems();

        this.classList.remove("updating");
        this.revealStickyHeaderAndOpen() || this.open();
    }

    revealStickyHeaderAndOpen(openedBy) {
        const sticky = this.closest("sticky-header");
        const header = sticky && sticky.header;

        // sticky header already revealed
        if (header && header.classList.contains("section-header-sticky"))
            return false;

        if (sticky && sticky.reveal) {
            let revealDuration =
                header && getComputedStyle(header).transitionDuration;

            if (revealDuration.endsWith("s")) {
                // css value in seconds
                sticky.reveal();
                setTimeout(
                    this.open.bind(this, openedBy),
                    parseFloat(revealDuration) * 1000
                );

                return true;
            }
        }

        return false;
    }

    getSectionsToRender() {
        return [
            {
                id: "cart-icon-bubble",
                section: "cart-icon-bubble",
            },
            {
                id: "cart-sidebar-items",
                section: "cart-sidebar-items",
            },
            {
                id: "cart-sidebar-footer",
                section: "cart-sidebar-footer",
            },
            {
                id: "cart-sidebar-shipping-bar",
                section: "cart-sidebar-shipping-bar",
            },
        ];
    }

    getSectionInnerHTML(html, selector = ".shopify-section") {
        return new DOMParser()
            .parseFromString(html, "text/html")
            .querySelector(selector).innerHTML;
    }

    updateSections(source = {}) {
        this.getSectionsToRender().forEach(({ id, section, selector }) => {
            const target = document.getElementById(id);
            if (!target || !source[section]) return;

            const el = target.querySelector(selector) || target;
            el.innerHTML = this.getSectionInnerHTML(source[section], selector);
        });

        this.renderSavedItems();
    }

    reload() {
        this.classList.add("updating");

        return fetch(
            window.location.pathname +
                "?sections=" +
                this.getSectionsToRender()
                    .map((section) => section.id)
                    .join(",")
        )
            .then((response) => response.text())
            .then((text) => JSON.parse(text))
            .then((json) => {
                this.updateSections(json);
                this.classList.remove("updating");

                return json;
            });
    }

    bindCartUpdate() {
        const debouncedOnChange = debounce((event) => {
            const input = event.target;

            this.classList.add("updating");
            this.updateQuantity(input.dataset.line, input.value);
        }, 500);

        this.addEventListener("click", (event) => {
            const saveButton = event.target.closest("[data-save-for-later]");
            if (saveButton) {
                event.preventDefault();
                this.saveForLater(saveButton);
                return;
            }

            const restoreButton = event.target.closest("[data-restore-saved]");
            if (restoreButton) {
                event.preventDefault();
                this.restoreSavedItem(restoreButton);
                return;
            }

            const link = event.target.closest("a[href]");

            if (!link || link.href.indexOf(routes.cart_change_url) == -1)
                return;

            event.preventDefault();
            this.classList.add("updating");
            this.updateQuantity(
                new URL(link.href).searchParams.get("line"),
                new URL(link.href).searchParams.get("quantity")
            );
        });

        // listen qunatity change via input
        this.addEventListener("input", debouncedOnChange);
    }

    getSavedItems() {
        try {
            const saved = JSON.parse(
                localStorage.getItem("supples_saved_for_later_v1") || "[]"
            );
            return Array.isArray(saved) ? saved : [];
        } catch (error) {
            return [];
        }
    }

    setSavedItems(items) {
        try {
            localStorage.setItem(
                "supples_saved_for_later_v1",
                JSON.stringify(items)
            );
        } catch (error) {
            console.error("Unable to save cart items", error);
        }
    }

    saveForLater(button) {
        const cartItem = button.closest(".cart-sidebar__item");
        if (!cartItem || button.disabled) return;

        const line = Number(cartItem.dataset.line);
        const variantId = Number(cartItem.dataset.variantId);
        if (!Number.isInteger(line) || !Number.isInteger(variantId)) return;

        const savedItem = {
            variantId,
            quantity: Math.max(1, Number(cartItem.dataset.quantity) || 1),
            title: cartItem.dataset.productTitle || "",
            variantTitle: cartItem.dataset.variantTitle || "",
            url: cartItem.dataset.productUrl || "#",
            image: cartItem.dataset.productImage || "",
            price: cartItem.dataset.productPrice || "",
        };
        const body = JSON.stringify({
            line,
            quantity: 0,
            sections: this.getSectionsToRender().map((section) => section.id),
            sections_url: window.location.pathname,
        });

        button.disabled = true;
        this.classList.add("updating");
        fetch(routes.cart_change_url, { ...fetchConfig(), body })
            .then((response) => response.json())
            .then((response) => {
                if (response.errors || response.status) throw response;

                const saved = this.getSavedItems().filter(
                    (item) => Number(item.variantId) !== variantId
                );
                saved.unshift(savedItem);
                this.setSavedItems(saved);
                this.updateSections(response.sections || {});
            })
            .catch((error) => {
                button.disabled = false;
                console.error("Save for later failed", error);
            })
            .finally(() => {
                this.classList.remove("updating");
                this.reinitPreloadImages();
            });
    }

    restoreSavedItem(button) {
        const variantId = Number(button.dataset.restoreSaved);
        const saved = this.getSavedItems();
        const savedItem = saved.find(
            (item) => Number(item.variantId) === variantId
        );
        if (!savedItem || button.disabled) return;

        const body = JSON.stringify({
            items: [
                {
                    id: variantId,
                    quantity: Math.max(1, Number(savedItem.quantity) || 1),
                },
            ],
            sections: this.getSectionsToRender().map((section) => section.id),
            sections_url: window.location.pathname,
        });

        button.disabled = true;
        this.classList.add("updating");
        fetch(routes.cart_add_url, { ...fetchConfig(), body })
            .then((response) => response.json())
            .then((response) => {
                if (response.errors || response.status) throw response;

                this.setSavedItems(
                    saved.filter((item) => Number(item.variantId) !== variantId)
                );
                this.updateSections(response.sections || {});
            })
            .catch((error) => {
                button.disabled = false;
                console.error("Restore saved item failed", error);
            })
            .finally(() => {
                this.classList.remove("updating");
                this.reinitPreloadImages();
            });
    }

    renderSavedItems() {
        const container = this.querySelector("[data-saved-container]");
        const list = container?.querySelector("[data-saved-items]");
        const count = container?.querySelector("[data-saved-count]");
        if (!container || !list || !count) return;

        const saved = this.getSavedItems();
        list.replaceChildren();
        count.textContent = String(saved.length);
        container.hidden = saved.length === 0;

        saved.forEach((item) => {
            const card = document.createElement("div");
            card.className = "cart-sidebar__saved-item";

            const media = document.createElement("a");
            media.className = "cart-sidebar__saved-media";
            media.href = item.url || "#";
            if (item.image) {
                const image = document.createElement("img");
                image.src = item.image;
                image.alt = "";
                image.loading = "lazy";
                media.appendChild(image);
            }

            const info = document.createElement("div");
            info.className = "cart-sidebar__saved-info";
            const title = document.createElement("a");
            title.className = "cart-sidebar__saved-title";
            title.href = item.url || "#";
            title.textContent = item.title || "";
            const price = document.createElement("div");
            price.className = "cart-sidebar__saved-price";
            price.textContent = item.price || "";
            const restore = document.createElement("button");
            restore.type = "button";
            restore.className = "cart-sidebar__saved-restore";
            restore.dataset.restoreSaved = String(item.variantId);
            restore.textContent = container.dataset.addLabel || "";

            info.append(title, price, restore);
            card.append(media, info);
            list.appendChild(card);
        });
    }

    updateQuantity(line, quantity) {
        const rawLine = line;
        const rawQuantity = quantity;

        line = Number(line);
        quantity = Number(quantity);

        const isValidLine = Number.isInteger(line) && line > 0;
        const isValidQuantity = Number.isInteger(quantity) && quantity >= 0;

        // do NOT early return — let Shopify respond so sections stay in sync
        const body = JSON.stringify({
            ...(isValidLine ? { line } : {}),
            ...(isValidQuantity ? { quantity } : {}),
            sections: this.getSectionsToRender().map((s) => s.id),
        });

        fetch(routes.cart_change_url, { ...fetchConfig(), body })
            .then((r) => r.json())
            .then((response) => {
                // update sections if it's exist
                if (response?.sections) {
                    this.updateSections(response.sections);
                }
                // responce error
                if (response?.errors) {
                    let message = response.errors;
                    if (message === "Cart Error") {
                        message = window.cartStrings.quantityError;
                    }
                    // sometimes response returns "no valid line parameter" with status "bad_request"
                    // end it does not return even section
                    // we need reload the page to remove the item from the minicart
                    // temporarely and ugly solution
                    if (message.includes("parameter is invalid")) {
                        window.location.reload();
                        return;
                    }

                    this.renderQtyError(isValidLine ? line : null, message);
                }
                // error on QTY
                if (isValidLine && isValidQuantity && quantity > 0) {
                    const item = response.items?.[line - 1];
                    if (item && item.quantity !== quantity) {
                        this.renderQtyError(
                            line,
                            window.cartStrings.quantityError
                        );
                    }
                }
            })
            .catch((err) => {
                console.error("Cart-sidebar updates failed", err);
            })
            .finally(() => {
                this.classList.remove("updating");
                this.reinitPreloadImages();
            });
    }

    renderQtyError(item, error) {
        const cartItems = document.getElementById("cart-sidebar-items");
        const cartItem = cartItems.querySelector(
            `.cart-sidebar__item[data-line="${item}"]`
        );
        let message = this.querySelector(".message-wrapper");
        if (!message) {
            message = createFromTemplate("message-wrapper");
        }

        cartItems.insertBefore(message, cartItem);
        message.querySelector(".error-message").innerHTML = error;
        message.removeAttribute("hidden");
    }

    setActiveElement(element) {
        this.activeElement = element;
    }
}

customElements.define("cart-sidebar", cartSidebar);
